// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { StandaloneMarkdown, markdownComponents, TableExportEnabledContext } from "./markdown-text";
import { IncrementalMarkdown } from "./IncrementalMarkdown";
import chatEvents from '../../lib/chatEvents';
import { attachmentUrl } from '../../lib/attachments';
// Import VoiceContext directly for safe optional access (no throw if missing)
import { VoiceContext } from '../../lib/voice/VoiceProvider';
import {
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useMessageRuntime,
  useThreadRuntime,
  useComposerRuntime,
} from "@assistant-ui/react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useState, useRef, useContext, useEffect, createContext, memo, useCallback } from "react";
import { useGlideText } from "../../hooks/useGlideText";
import QuestionCard from "./QuestionCard";
import ConfiguratorForm from "./ConfiguratorForm";
import WriteConfirmCard from "./WriteConfirmCard";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import MicIcon from "@mui/icons-material/Mic";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import FolderIcon from "@mui/icons-material/Folder";
import CloudIcon from "@mui/icons-material/Cloud";
import MailIcon from "@mui/icons-material/Mail";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ShieldIcon from "@mui/icons-material/Shield";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// Create local contexts that will be provided by parent components
export const FilesContext = createContext();
export const MessageFilesContext = createContext();
export const EmbeddedConfigContext = createContext();
// Context for shared thread info - provides user lookup for message attribution
// { isSharedThread: boolean, companyUsers: [{id, email, display_name}], currentUserId: string }
export const SharedThreadContext = createContext();

// Local Files (optional — provided only by ChatInterfaceFull, null in embedded).
// { enabled: boolean, folderName: string|null, needsPermission: boolean,
//   trusted: boolean, busy: boolean, onConnect: () => void,
//   onDisconnect: () => void, onToggleTrust: () => void }
export const LocalFilesContext = createContext(null);

// GPT selector in the input box (optional — provided only by ChatInterfaceFull).
// { gpts: [{id, name, favicon_url?}], selectedId: string|null, onSelect: (id) => void }
export const GptSelectorContext = createContext(null);

// Office 365 / OneDrive (optional — provided only by ChatInterfaceFull).
// { enabled: boolean, connected: boolean, accountName: string|null,
//   busy: boolean, onConnect: () => void, onDisconnect: () => void }
export const Office365Context = createContext(null);

// Gmail (optional — provided only by ChatInterfaceFull).
// Same shape as Office365Context.
export const GmailContext = createContext(null);

// Incognito chat (optional — false in embedded). True = the whole chat UI
// switches to the purple incognito treatment (tinted area, styled composer).
export const IncognitoContext = createContext(false);

// Global typewriter state - allows Composer to know if typewriter is still animating
// and to skip to end when stop is pressed
const typewriterState = {
  isAnimating: false,
  skipToEnd: () => {},
};

// Shared copy function for message content (handles charts by converting canvas to image)
async function copyMessageContent(contentElem) {
  if (!contentElem) return false;

  try {
    // Check if this message contains a chart (canvas element)
    const canvasElements = contentElem.querySelectorAll('canvas');

    if (canvasElements.length > 0) {
      // Clone the content element to modify it without affecting the display
      const clonedContent = contentElem.cloneNode(true);

      // Convert each canvas to an image
      const clonedCanvases = clonedContent.querySelectorAll('canvas');
      canvasElements.forEach((originalCanvas, index) => {
        try {
          // Get the canvas as a PNG data URL
          const dataUrl = originalCanvas.toDataURL('image/png');

          // Create an img element
          const img = document.createElement('img');
          img.src = dataUrl;
          img.style.cssText = originalCanvas.style.cssText;
          img.alt = 'Chart visualization';

          // Replace canvas with img in the clone
          clonedCanvases[index].parentNode.replaceChild(img, clonedCanvases[index]);
        } catch (err) {
          console.error(`Failed to convert canvas ${index + 1}:`, err);
        }
      });

      // Now copy the modified HTML with images instead of canvases
      const selection = window.getSelection();
      const range = document.createRange();

      // Temporarily append the clone to the DOM (required for selection)
      clonedContent.style.position = 'absolute';
      clonedContent.style.left = '-9999px';
      document.body.appendChild(clonedContent);

      range.selectNodeContents(clonedContent);
      selection.removeAllRanges();
      selection.addRange(range);

      const successful = document.execCommand('copy');

      // Clean up
      selection.removeAllRanges();
      document.body.removeChild(clonedContent);

      if (!successful) {
        throw new Error('execCommand copy failed');
      }
    } else {
      // No canvas elements - use normal copy
      const selection = window.getSelection();
      const range = document.createRange();

      range.selectNodeContents(contentElem);
      selection.removeAllRanges();
      selection.addRange(range);

      const successful = document.execCommand('copy');
      selection.removeAllRanges();

      if (!successful) {
        throw new Error('execCommand copy failed');
      }
    }
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    // Fallback to plain text
    try {
      const text = contentElem.textContent.trim();
      await navigator.clipboard.writeText(text);
      return true;
    } catch (fallbackErr) {
      console.error('Fallback copy also failed:', fallbackErr);
      return false;
    }
  }
}

// Helper to get user display name from SharedThreadContext
// Returns author name for shared threads, null for non-shared threads
function useMessageAuthor(userId, messageRole) {
  const sharedThreadInfo = useContext(SharedThreadContext);
  if (!sharedThreadInfo?.isSharedThread) return null;

  // Assistant messages don't have authors (they're from the AI)
  if (messageRole === 'assistant') return null;

  // User messages without user_id (old messages before we tracked this)
  if (!userId) return null;

  const { companyUsers, currentUserId } = sharedThreadInfo;

  if (userId === currentUserId) return 'You';

  const user = companyUsers?.find(u => u.id === userId);
  if (user) {
    return user.display_name || user.email?.split('@')[0] || 'Unknown';
  }

  // User not in company users list (could be from impersonation or deleted user)
  return 'Unknown';
}

// Static component for rendering historical messages (not using runtime)
// Returns array of elements directly (no wrapper div) so :last-child CSS works
function HistoryMessages({ messages }) {
  // Subscribe to SharedThreadContext to re-render when companyUsers loads
  const sharedThreadInfo = useContext(SharedThreadContext);

  if (!messages || messages.length === 0) return null;

  return (
    <>
      {messages.map((msg, index) => (
        msg.role === 'user' ? (
          <HistoryUserMessage key={msg.id || index} id={msg.id} content={msg.content} attachments={msg.attachments || []} userId={msg.user_id} />
        ) : (
          <HistoryAssistantMessage key={msg.id || index} id={msg.id} content={msg.content} metadata={msg.metadata} userId={msg.user_id} />
        )
      ))}
    </>
  );
}

function HistoryUserMessage({ id, content, attachments = [], userId }) {
  const embeddedConfig = useContext(EmbeddedConfigContext);
  const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';
  const authorName = useMessageAuthor(userId, 'user');

  // Normalize content to string - content can be string (old format) or array of parts (new format)
  const getTextContent = () => {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      return content
        .filter(part => part.type === 'text')
        .map(part => part.text)
        .join('\n');
    }
    return '';
  };
  const textContent = getTextContent();

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm('Delete this message?')) return;
    if (!id) {
      alert('Cannot delete message - ID not found');
      return;
    }

    // Capture the message element before async operation (e.currentTarget becomes stale after await)
    const messageElement = e.currentTarget.closest('[data-role="user"]');

    try {
      const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

      // Use different endpoint and auth for embedded vs full mode
      let response;
      if (embeddedConfig?.isEmbedded && embeddedConfig?.shareToken && embeddedConfig?.customGptId) {
        // Embedded mode: use public endpoint with share token
        response = await fetch(`${API_URL}/public/cme/${embeddedConfig.customGptId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: id, mode: embeddedConfig.mode || 'full' },
            token: embeddedConfig.shareToken
          })
        });
      } else {
        // Full mode: use protected endpoint with auth token
        const token = localStorage.getItem('cme_token');
        response = await fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: id, mode: 'full' }
          })
        });
      }

      if (response.ok) {
        messageElement.remove();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message');
    }
  };

  return (
    <Box
      className="chat-message-enter chat-message user-message"
      data-role="user"
      data-message-id={id}
      sx={{ display: 'flex', justifyContent: 'flex-end' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '75%' }}>
        <Paper
          elevation={0}
          sx={{
            // theme's user-bubble colors when it defines them, neutral otherwise
            bgcolor: (t) => t.palette.background.userBubble || t.palette.action.hover,
            border: (t) => (t.palette.background.userBubbleBorder
              ? `1px solid ${t.palette.background.userBubbleBorder}` : undefined),
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {/* File attachments - render before text */}
          {attachments.length > 0 && (
            <Box sx={{ p: 1.5, pb: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {attachments.map((att, index) => {
                const isImage = att.content_type && att.content_type.startsWith('image/');
                const imageUrl = attachmentUrl(att.id);

                if (isImage) {
                  return (
                    <Box key={index} sx={{ overflow: 'hidden', borderRadius: 3 }}>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={att.filename}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                          maxWidth: 250,
                          maxHeight: 200,
                        }}
                      />
                    </Box>
                  );
                } else {
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        p: 1,
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">{att.filename}</Typography>
                    </Box>
                  );
                }
              })}
            </Box>
          )}
          <Box
            className="chat-user-text"
            sx={{
              // a filled (solid-color) bubble brings its own ink
              color: (t) => t.palette.background.userBubbleText || t.palette.text.primary,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              p: 1.5,
            }}
          >
            {textContent}
          </Box>
        </Paper>
        {/* Tools container (visible on hover) for user messages */}
        {id && (
          <Box
            className="message-tools-container"
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            {authorName && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  mr: 0.5,
                }}
              >
                By - {authorName}
              </Typography>
            )}
            <Tooltip title="Delete message">
              <IconButton
                onClick={handleDelete}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function HistoryAssistantMessage({ id, content, metadata, userId }) {
  const contentRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const embeddedConfig = useContext(EmbeddedConfigContext);
  const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';
  const authorName = useMessageAuthor(userId, 'assistant');

  // Normalize content to string for legacy handling
  // Content can be: string (old format) or array of parts (new format)
  const getTextContent = () => {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      return content
        .filter(part => part.type === 'text')
        .map(part => part.text)
        .join('\n');
    }
    return '';
  };
  const textContent = getTextContent();

  // Handle chart rendering
  useEffect(() => {
    if (!contentRef.current || !metadata?.chart_html) return;
    if (!textContent.startsWith('[Chart:')) return;

    console.log(`📊 [CHART] Rendering chart from history: ${metadata.chart_id}`);

    // Extract script content and HTML separately
    const scriptMatch = metadata.chart_html.match(/<script>([\s\S]*?)<\/script>/);
    const htmlOnly = metadata.chart_html.replace(/<script>[\s\S]*?<\/script>/, '');

    // Insert HTML
    contentRef.current.innerHTML = htmlOnly;

    // Execute script manually after DOM update
    if (scriptMatch && scriptMatch[1]) {
      setTimeout(() => {
        try {
          // Destroy any existing chart on this canvas first
          const canvas = contentRef.current.querySelector('canvas');
          if (canvas && window.Chart) {
            const existingChart = window.Chart.getChart(canvas);
            if (existingChart) {
              existingChart.destroy();
            }
          }
          eval(scriptMatch[1]);
          console.log(`✅ [CHART] Rendered ${metadata.chart_type} chart from history`);
        } catch (error) {
          console.error(`❌ [CHART] Error initializing chart from history:`, error);
        }
      }, 0);
    }
  }, [content, metadata]);

  const handleCopy = async (e) => {
    e.stopPropagation();
    const messageRoot = e.currentTarget.closest('[data-role="assistant"]');
    const contentElem = messageRoot.querySelector('.chat-assistant-text');
    const success = await copyMessageContent(contentElem);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm('Delete this message?')) return;
    if (!id) {
      alert('Cannot delete message - ID not found');
      return;
    }

    // Capture the message element before async operation (e.currentTarget becomes stale after await)
    const messageElement = e.currentTarget.closest('[data-role="assistant"]');

    try {
      const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

      // Use different endpoint and auth for embedded vs full mode
      let response;
      if (embeddedConfig?.isEmbedded && embeddedConfig?.shareToken && embeddedConfig?.customGptId) {
        // Embedded mode: use public endpoint with share token
        response = await fetch(`${API_URL}/public/cme/${embeddedConfig.customGptId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: id, mode: embeddedConfig.mode || 'full' },
            token: embeddedConfig.shareToken
          })
        });
      } else {
        // Full mode: use protected endpoint with auth token
        const token = localStorage.getItem('cme_token');
        response = await fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: id, mode: 'single' }
          })
        });
      }

      if (response.ok) {
        messageElement.remove();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message');
    }
  };

  // Check if this is a chart message
  const isChart = textContent.startsWith('[Chart:') && metadata?.chart_html;

  // Check if this is a question_card message (ask_user tool, replayed from history)
  const isQuestion = textContent.startsWith('[Question:') && metadata?.question_card;

  // Check if this is a form_card message (present_form tool, replayed from history)
  const isForm = textContent.startsWith('[Form:') && metadata?.form_card;

  // Check if content has images (new format)
  const hasImages = Array.isArray(content) && content.some(part => part.type === 'image');

  // Detect file placeholders saved by the backend after a successful export.
  // Format: __FILE_PLACEHOLDER__:<format>:<filename>  (one per line)
  // The actual file_data is ephemeral, so in history we render a disabled card.
  const FILE_PLACEHOLDER_RE = /__FILE_PLACEHOLDER__:([^:\n]*):([^\n]+)/g;
  const parseFilePlaceholders = (text) => {
    if (!text || !text.includes('__FILE_PLACEHOLDER__:')) return null;
    const matches = [...text.matchAll(FILE_PLACEHOLDER_RE)];
    if (matches.length === 0) return null;
    return matches.map(m => ({ format: m[1] || '', filename: m[2].trim() }));
  };

  // Render content parts (handles text, images, and file placeholders)
  const renderContentParts = () => {
    if (typeof content === 'string') {
      const placeholders = parseFilePlaceholders(content);
      if (placeholders) {
        // Strip placeholder lines from text; render text (if any) + cards
        const remaining = content.replace(FILE_PLACEHOLDER_RE, '').trim();
        return (
          <>
            {remaining && <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{remaining}</Markdown>}
            {placeholders.map((p, i) => (
              <HistoryFilePlaceholderCard key={i} filename={p.filename} format={p.format} />
            ))}
          </>
        );
      }
      return <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</Markdown>;
    }

    if (Array.isArray(content)) {
      return content.map((part, index) => {
        if (part.type === 'text') {
          const placeholders = parseFilePlaceholders(part.text);
          if (placeholders) {
            const remaining = part.text.replace(FILE_PLACEHOLDER_RE, '').trim();
            // Wrap in a Box (Fragment-with-key alternative without React import)
            return (
              <Box key={index} sx={{ display: 'contents' }}>
                {remaining && <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{remaining}</Markdown>}
                {placeholders.map((p, i) => (
                  <HistoryFilePlaceholderCard key={`${index}-${i}`} filename={p.filename} format={p.format} />
                ))}
              </Box>
            );
          }
          return <Markdown key={index} remarkPlugins={[remarkGfm]} components={markdownComponents}>{part.text}</Markdown>;
        }
        if (part.type === 'image' && part.attachment_id) {
          const imageUrl = attachmentUrl(part.attachment_id);
          return (
            <Box key={index} sx={{ my: 2 }}>
              <Box
                component="img"
                src={imageUrl}
                alt="Generated image"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 512,
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              />
            </Box>
          );
        }
        return null;
      });
    }

    return null;
  };

  return (
    <Box
      className="chat-message-enter chat-message assistant-message"
      data-role="assistant"
      data-message-id={id}
      sx={{ display: 'flex' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Saved activity of the turn — outside the content box: chart
            replay rewrites that box's innerHTML */}
        {savedActivity(metadata) && (
          <ActivityBlock timeline={savedActivity(metadata).timeline} durationMs={savedActivity(metadata).duration_ms || 0} />
        )}
        <Box
          ref={contentRef}
          className="chat-assistant-text aui-md"
          sx={{
            color: 'text.primary',
            maxWidth: 'none',
            wordBreak: 'break-word',
          }}
        >
          {/* Only render content if not a chart (chart will be rendered via useEffect) */}
          {!isChart && !isQuestion && !isForm && renderContentParts()}
          {/* Replay a question_card from history in disabled mode */}
          {isQuestion && (
            <QuestionCard
              question={metadata.question_card.question}
              description={metadata.question_card.description}
              options={metadata.question_card.options}
              multiple={metadata.question_card.multiple}
              cardId={metadata.question_card.card_id}
              disabled
            />
          )}
          {/* Replay a form_card from history in disabled (submitted) mode */}
          {isForm && (
            <ConfiguratorForm
              title={metadata.form_card.title}
              description={metadata.form_card.description}
              fields={metadata.form_card.fields}
              submitLabel={metadata.form_card.submit_label}
              formId={metadata.form_card.form_id}
              disabled
            />
          )}
        </Box>
        {/* Tools container (visible on hover) for assistant messages */}
        {id && (
          <Box
            className="message-tools-container"
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            <Tooltip title={copied ? "Copied!" : "Copy message"}>
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
              >
                {copied ? <CheckIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete message">
              <IconButton
                onClick={handleDelete}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            {authorName && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  ml: 0.5,
                }}
              >
                By - {authorName}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export const Thread = ({ historyMessages = [], conversationStartersSlot = null, welcomeSlot = null, forceWelcome = false, welcomeReopenKey = 0, onWelcomeReopen = null, onNewChat = null, activeModelName = null, activeModelIcon = null }) => {
  // Track which history array we've scrolled for (by reference)
  const scrolledForRef = useRef(null);

  // Guide re-opened via the button: scroll the viewport to the top so the
  // welcome guide (rendered above the messages) is actually visible.
  // Delayed + instant: the browser's scroll anchoring re-adjusts right after
  // the guide is inserted above the messages — a smooth/immediate scroll
  // races against it and sometimes loses.
  const prevReopenKeyRef = useRef(welcomeReopenKey);
  useEffect(() => {
    if (welcomeReopenKey === prevReopenKeyRef.current) return;
    prevReopenKeyRef.current = welcomeReopenKey;
    if (!forceWelcome) return;
    const vp = document.getElementById('chatScrollContainer');
    if (!vp) return;
    const timer = setTimeout(() => vp.scrollTo({ top: 0, behavior: 'auto' }), 100);
    return () => clearTimeout(timer);
  }, [welcomeReopenKey, forceWelcome]);

  // Scroll to bottom once when history with messages is loaded
  useEffect(() => {
    // Skip if no messages or already scrolled for this exact array
    if (!historyMessages || historyMessages.length === 0) return;
    if (scrolledForRef.current === historyMessages) return;

    // Mark this array as handled
    scrolledForRef.current = historyMessages;

    const viewport = document.getElementById('chatScrollContainer');
    if (!viewport) return;

    const scrollToBottom = () => {
      if (viewport.scrollHeight > viewport.clientHeight) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    };

    // Find all images in the viewport
    const images = viewport.querySelectorAll('img');

    if (images.length === 0) {
      // No images - scroll immediately
      scrollToBottom();
      return;
    }

    // Wait for all images to load before scrolling
    let loadedCount = 0;
    const totalCount = images.length;
    let hasScrolled = false;

    const checkAndScroll = () => {
      loadedCount++;
      if (loadedCount >= totalCount && !hasScrolled) {
        hasScrolled = true;
        scrollToBottom();
      }
    };

    images.forEach(img => {
      if (img.complete) {
        checkAndScroll();
      } else {
        img.addEventListener('load', checkAndScroll, { once: true });
        img.addEventListener('error', checkAndScroll, { once: true });
      }
    });

    // Fallback: scroll after 2 seconds regardless
    const fallbackTimer = setTimeout(() => {
      if (!hasScrolled) {
        hasScrolled = true;
        scrollToBottom();
      }
    }, 2000);

    return () => clearTimeout(fallbackTimer);
  }, [historyMessages]);

  // Drag & drop file attachment: dropping files anywhere over the chat adds
  // them to the composer instead of letting the browser open them.
  const filesContext = useContext(FilesContext);
  const embeddedConfig = useContext(EmbeddedConfigContext);
  const isIncognito = useContext(IncognitoContext);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (embeddedConfig?.hideFileUpload) return;

    let dragDepth = 0;

    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

    const onDragEnter = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth++;
      setIsDragging(true);
    };
    const onDragOver = (e) => {
      if (!hasFiles(e)) return;
      // preventDefault on dragover is what allows the drop AND stops the
      // browser from navigating to the dropped file.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e) => {
      if (!hasFiles(e)) return;
      dragDepth = Math.max(0, dragDepth - 1);
      if (dragDepth === 0) setIsDragging(false);
    };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth = 0;
      setIsDragging(false);
      const dropped = Array.from(e.dataTransfer.files || []);
      if (dropped.length && filesContext?.setSelectedFiles) {
        filesContext.setSelectedFiles(prev => [...prev, ...dropped]);
      }
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [embeddedConfig, filesContext]);

  return (
    <ThreadPrimitive.Root id="chat-area" className={`chat-area${isIncognito ? ' incognito' : ''}`}>
      {/* Drag & drop overlay — visual only, pointer-events none so the drop
          bubbles to the window listeners */}
      {isDragging && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(120, 120, 130, 0.08)',
            backdropFilter: 'blur(3px)',
            pointerEvents: 'none',
            animation: 'dropFadeIn 150ms ease-out',
            '@keyframes dropFadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              px: 5,
              py: 4,
              borderRadius: 4,
              bgcolor: 'background.paper',
              border: '2px dashed',
              borderColor: 'grey.400',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              pointerEvents: 'none',
              animation: 'dropPop 180ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              '@keyframes dropPop': {
                from: { transform: 'scale(0.92)', opacity: 0 },
                to: { transform: 'scale(1)', opacity: 1 },
              },
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
                color: 'grey.700',
                animation: 'dropFloat 1.2s ease-in-out infinite',
                '@keyframes dropFloat': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-6px)' },
                },
              }}
            >
              <UploadFileIcon sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Drop to attach
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Release to add files to your message
            </Typography>
          </Paper>
        </Box>
      )}
      {/* Messages - IDs match EMBED_DOCUMENTATION.md selectors */}
      <ThreadPrimitive.Viewport id="chatScrollContainer" className="chat-viewport">
        {welcomeSlot && (forceWelcome ? (
          <div
            id="chatWelcome"
            className="chat-welcome"
            key={`forced-${welcomeReopenKey}`}
            style={{ overflowAnchor: 'none' }}
          >
            {welcomeSlot}
          </div>
        ) : (
          <ThreadPrimitive.If empty>
            <div id="chatWelcome" className="chat-welcome">
              {welcomeSlot}
            </div>
          </ThreadPrimitive.If>
        ))}
        <div id="chatContainer" className="chat-messages-content">
          <HistoryMessages messages={historyMessages} />
          <ThreadPrimitive.Messages
            components={{
              UserMessage,
              AssistantMessage,
            }}
          />
        </div>
      </ThreadPrimitive.Viewport>

      {/* Starters - IDs match EMBED_DOCUMENTATION.md selectors */}
      {conversationStartersSlot && (
        <div id="conversationStarters" className="chat-starters">
          <div id="startersContainer" className="chat-starters-inner">
            {conversationStartersSlot}
          </div>
        </div>
      )}

      {/* Input */}
      <div id="chat-input" className="chat-input">
        <div className="chat-input-content">
          <Composer onWelcomeReopen={onWelcomeReopen} onNewChat={onNewChat} />
          <div
            id="chat-disclaimer"
            className="chat-disclaimer"
            style={{
              fontSize: '12px',
              color: 'var(--text-tertiary, #8e8ea0)',
              textAlign: 'center',
              padding: '6px 12px 4px',
              opacity: 0.75,
              userSelect: 'none',
            }}
          >
            AI can and will make mistakes. Please verify important information.
            {!embeddedConfig?.isEmbedded && (
              <span
                className="chat-build-version"
                style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
                title={`Frontend build ${__BUILD_VERSION__} · commit ${__BUILD_HASH__}`}
              >
                · v{__BUILD_VERSION__}
              </span>
            )}
          </div>
        </div>
      </div>
    </ThreadPrimitive.Root>
  );
};

const Composer = ({ onWelcomeReopen = null, onNewChat = null }) => {
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const composerRuntime = useComposerRuntime();
  const threadRuntime = useThreadRuntime();
  // The autosizing input measures the placeholder when empty: on a narrow
  // screen (or a wide font) a wrapped placeholder makes the empty box two
  // lines tall. Empty = one row; the placeholder is truncated in CSS.
  const [composerEmpty, setComposerEmpty] = useState(() => !composerRuntime.getState().text);
  useEffect(() => composerRuntime.subscribe(() => {
    setComposerEmpty(!composerRuntime.getState().text);
  }), [composerRuntime]);
  const filesContext = useContext(FilesContext);
  const { selectedFiles, setSelectedFiles } = filesContext || { selectedFiles: [], setSelectedFiles: () => {} };
  const [browserListening, setBrowserListening] = useState(false);
  const [isTypewriterAnimating, setIsTypewriterAnimating] = useState(false);
  const [sttLang, setSttLang] = useState('');  // '' = auto-detect
  const [showLangMenu, setShowLangMenu] = useState(false);
  const micBtnRef = useRef(null);
  const longPressedRef = useRef(false);
  const micLongPressTimer = useRef(null);
  const micLongPressSuppress = useRef(false);

  // Get embedded config if available
  const embeddedConfig = useContext(EmbeddedConfigContext);
  // Incognito chat (provided by ChatInterfaceFull) — purple composer treatment
  const isIncognito = useContext(IncognitoContext);

  // VoiceProvider (optional — null if no VoiceProvider wraps this component)
  const voice = useContext(VoiceContext);
  const isListening = voice ? voice.listening : browserListening;

  // Local Files (optional — null in embedded chat; provided by ChatInterfaceFull)
  const localFiles = useContext(LocalFilesContext);
  // GPT selector (optional — null in embedded chat)
  const gptSelector = useContext(GptSelectorContext);
  // GPT selector dialog state (centered popup)
  const [gptDialogOpen, setGptDialogOpen] = useState(false);
  // Office 365 / OneDrive (optional — null in embedded chat)
  const office365 = useContext(Office365Context);
  // Gmail (optional — null in embedded chat)
  const gmail = useContext(GmailContext);
  // "+" menu state (upload / local folder / office 365 / gmail)
  const [plusMenuAnchor, setPlusMenuAnchor] = useState(null);
  // Accounts dialog state: 'o365' | 'gmail' | null
  const [acctOpen, setAcctOpen] = useState(null);

  // Poll typewriter state to know when to show stop button
  useEffect(() => {
    const interval = setInterval(() => {
      if (typewriterState.isAnimating !== isTypewriterAnimating) {
        setIsTypewriterAnimating(typewriterState.isAnimating);
      }
    }, 50); // Check every 50ms
    return () => clearInterval(interval);
  }, [isTypewriterAnimating]);

  // iPad keyboard dismiss fix - when keyboard hides via keyboard button, scroll to fix viewport
  // Only apply to iPad, not iPhone (iPhone handles this differently)
  useEffect(() => {
    const isIPad = /iPad/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && window.innerWidth >= 768);

    if (!isIPad) return;

    // Use visualViewport API to detect keyboard dismiss
    const handleViewportResize = () => {
      // When keyboard closes, visualViewport height increases back to window height
      // Scroll to reset the viewport position
      if (window.visualViewport && window.visualViewport.height >= window.innerHeight * 0.9) {
        // Small delay to let iOS finish its animation
        setTimeout(() => {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }, 100);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      return () => {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      };
    }
  }, []);

  // Handle stop button click when typewriter is animating
  const handleStopTypewriter = () => {
    if (typewriterState.skipToEnd) {
      typewriterState.skipToEnd();
      setIsTypewriterAnimating(false);
    }
    // Focus the textarea after stopping
    const textarea = document.getElementById('messageInput');
    if (textarea) {
      textarea.focus();
    }
  };

  // Focus textarea (used when clicking disabled send button)
  const focusInput = () => {
    setTimeout(() => {
      const textarea = document.getElementById('messageInput');
      if (textarea) {
        textarea.focus();
      }
    }, 50);
  };

  // Blur input to close keyboard after sending on touch devices (mobile/tablet).
  // Use setTimeout to blur after assistant-ui's internal focus logic.
  const blurOnTouch = () => {
    const isTouchDevice = 'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      /iPad|iPhone|iPod|Android/i.test(navigator.userAgent);
    if (isTouchDevice) {
      setTimeout(() => {
        const textarea = document.getElementById('messageInput');
        if (textarea) {
          textarea.blur();
        }
      }, 100);
    }
  };

  // Remove the trailing assistant message when a run is aborted before it
  // produced real content — only the thinking pulse and/or status chips
  // are left. Runs on Stop button and on ESC (both cancel the run).
  const removeAbortedPlaceholder = () => {
    setTimeout(() => {
      const assistantMessages = document.querySelectorAll('[data-role="assistant"]');
      if (assistantMessages.length === 0) return;
      const last = assistantMessages[assistantMessages.length - 1];
      const container = last.querySelector('.assistant-content-container');
      if (!container) return;
      const realContent = container.querySelectorAll(
        ':scope > *:not(.status-display):not(.thinking-pulse)'
      );
      if (realContent.length === 0) {
        last.remove();
      }
    }, 50); // Small delay to let the cancel action complete
  };

  // Handle stop/cancel button - remove empty placeholder message and focus input
  const handleStopAndFocus = () => {
    removeAbortedPlaceholder();
    setTimeout(() => {
      const textarea = document.getElementById('messageInput');
      if (textarea) {
        textarea.focus();
      }
    }, 50);
  };

  // ESC also cancels the run (assistant-ui built-in) — clean the placeholder too
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        removeAbortedPlaceholder();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
    // Reset input
    e.target.value = '';
  };

  const handlePaste = async (e) => {
    // Disable paste if hidePaste is true in embedded mode
    if (embeddedConfig?.hidePaste) {
      return;
    }

    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: item.type });
          imageFiles.push(file);
        }
      }
    }

    if (imageFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...imageFiles]);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Voice input setup — uses SttPlayer (via chatEvents) if microphone tool configured, else browser SpeechRecognition
  const usingSttPlayer = !!voice || !!embeddedConfig?.hasMicrophone;

  // Helper: set textarea value programmatically
  function setTextareaValue(text) {
    if (!inputRef.current) return;
    const textarea = inputRef.current.querySelector('textarea');
    if (!textarea) return;
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype, 'value'
    ).set;
    nativeInputValueSetter.call(textarea, text);
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // VoiceProvider STT: fill textarea as transcript updates (interim + final)
  const prevVoiceTranscriptRef = useRef('');
  useEffect(() => {
    if (!voice) return;
    if (voice.transcript && voice.transcript !== prevVoiceTranscriptRef.current) {
      prevVoiceTranscriptRef.current = voice.transcript;
      setTextareaValue(voice.transcript);
    }
    if (!voice.transcript) prevVoiceTranscriptRef.current = '';
  }, [voice?.transcript]);

  // Auto-send on silence/final auto-stop (autoStopTick increments).
  // Manual mic tap-to-stop does NOT set autoStopTick, so the text stays editable.
  const prevAutoStopRef = useRef(0);
  useEffect(() => {
    if (!voice) return;
    if (voice.autoStopTick === prevAutoStopRef.current) return;
    prevAutoStopRef.current = voice.autoStopTick;
    const text = voice.transcript?.trim();
    if (text && !voice.listening) {
      setTimeout(() => {
        const sendBtn = document.getElementById('sendButton');
        if (sendBtn) sendBtn.click();
      }, 100);
    }
  }, [voice?.autoStopTick, voice?.listening, voice?.transcript]);

  // Browser SpeechRecognition fallback (when no VoiceProvider and no microphone tool)
  useEffect(() => {
    if (voice || usingSttPlayer) return; // VoiceProvider or SttPlayer handles it

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      recognition.onstart = () => { setBrowserListening(true); };
      recognition.onend = () => { setBrowserListening(false); };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript;
        }
        if (finalTranscript) setTextareaValue(finalTranscript);
      };

      recognition.onerror = (event) => {
        setBrowserListening(false);
        const msgs = {
          'no-speech': 'No speech detected. Please try again.',
          'audio-capture': 'No microphone found or microphone access denied.',
          'not-allowed': 'Microphone access denied. Please grant permission.',
          'network': 'Network error occurred during voice recognition.',
        };
        alert(msgs[event.error] || `Voice recognition error: ${event.error}`);
      };

      recognitionRef.current = recognition;
    }

    return () => { if (recognitionRef.current) recognitionRef.current.stop(); };
  }, [usingSttPlayer]);

  const handleMicClick = () => {
    setShowLangMenu(false);

    // VoiceProvider mode
    if (voice && usingSttPlayer) {
      if (voice.listening) voice.stopListening();
      else { console.log('[Thread] mic click, sttLang:', sttLang); voice.setLang(sttLang); voice.startListening(); }
      return;
    }

    // Browser fallback
    if (!recognitionRef.current) {
      alert('Voice recognition is not supported in this browser.');
      return;
    }
    if (browserListening) recognitionRef.current.stop();
    else {
      try { recognitionRef.current.start(); }
      catch (e) { console.error('Speech recognition error:', e); }
    }
  };

  const isReadonly = embeddedConfig?.isReadonly;
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const check = () => {
      const w = window.innerWidth;
      if (w === 0) return;
      const coarse = window.matchMedia?.('(pointer: coarse)').matches;
      setIsMobile(w <= 768 || (coarse && w <= 1024));
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  useEffect(() => {
    if (!showLangMenu || !isMobile) return;
    const onDocPointerDown = (e) => {
      if (micBtnRef.current && !micBtnRef.current.contains(e.target)) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [showLangMenu, isMobile]);
  const hasMicOrVoice = usingSttPlayer || embeddedConfig?.hasVoice;

  return (
    <ComposerPrimitive.Root
      id="chatForm"
      onSubmit={(e) => {
        // Enter-to-send for file-only messages: attachments live in
        // FilesContext (not in the runtime composer), so assistant-ui's
        // built-in form submit no-ops on the empty composer and Enter
        // did nothing. Send directly through the runtime, mirroring the
        // send button. Text messages fall through to the default handler.
        if (selectedFiles.length > 0 && !threadRuntime.getState().isRunning) {
          e.preventDefault();
          composerRuntime.send();
          blurOnTouch();
        }
      }}
      style={{ position: 'relative', display: 'flex', flexDirection: 'column', width: '100%', gap: '8px' }}>
      {/* GPT selector chip — above the input so the user always knows who they're talking to */}
      {!isReadonly && gptSelector && gptSelector.gpts?.length > 0 && (() => {
        const selected = gptSelector.gpts.find(g => g.id === gptSelector.selectedId) || gptSelector.gpts.find(g => g.is_default) || gptSelector.gpts[0];
        const hasMultiple = gptSelector.gpts.length > 1;
        // Locked companies: the chip is informative only — requests are routed
        // by the default assistant; explicit choice lives on the Assistants page.
        const locked = !!gptSelector.locked;
        const clickable = hasMultiple && !locked;

        // Same avatar rendering as the sidebar GPTSelector
        const renderGptAvatar = (gpt, size = 20, iconSize = 12) => {
          if (gpt?.favicon_url) {
            return (
              <Box
                component="img"
                src={`${gpt.favicon_url}?t=${Date.now()}`}
                alt={gpt.name}
                sx={{ width: size, height: size, borderRadius: '50%' }}
              />
            );
          }
          const isDefault = gpt?.is_default;
          return (
            <Box sx={{
              width: size, height: size, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: isDefault
                ? 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)'
                : 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
            }}>
              {isDefault
                ? <ChatBubbleOutlineIcon sx={{ fontSize: iconSize, color: 'white' }} />
                : <BoltIcon sx={{ fontSize: iconSize, color: 'white' }} />}
            </Box>
          );
        };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
            <Tooltip
              title={locked ? 'Requests are routed automatically — pick an assistant from the Assistants page if you need a specific one' : hasMultiple ? 'Choose assistant' : ''}
              arrow
              disableHoverListener={!locked && !hasMultiple}
            >
              <Box
                onClick={clickable ? () => setGptDialogOpen(true) : undefined}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.4,
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: (t) => t.palette.background.chip || t.palette.action.selected,
                  cursor: clickable ? 'pointer' : 'default',
                  minWidth: 0,
                  maxWidth: 280,
                  '&:hover': clickable ? { bgcolor: 'action.hover' } : {},
                  transition: 'all 0.15s ease',
                }}
              >
                {renderGptAvatar(selected)}
                <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                  {selected?.name || 'General'}
                </Typography>
              </Box>
            </Tooltip>
            {/* New Chat — contextual to writing: start a clean conversation from the composer */}
            {!isReadonly && onNewChat && (
              <Tooltip title="New chat" arrow>
                <Box
                  onClick={onNewChat}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: (t) => t.palette.background.chip || t.palette.action.selected,
                    color: 'text.secondary',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'primary.main',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </Box>
              </Tooltip>
            )}
            {clickable && (() => {
              // Front-door assistant (concierge) + specialists — same split as the sidebar
              const autoGpt = gptSelector.gpts.find(g => g.is_default) || gptSelector.gpts[0];
              const specialists = gptSelector.gpts.filter(g => g.id !== autoGpt.id);
              const onAuto = gptSelector.selectedId === autoGpt.id || !gptSelector.selectedId;
              const rowSx = (active) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                borderRadius: 1.5,
                cursor: 'pointer',
                bgcolor: active ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'all 0.15s ease',
              });
              return (
              <Dialog
                open={gptDialogOpen}
                onClose={() => setGptDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, maxHeight: '80vh', p: 1.5 } }}
              >
                {/* Front-door assistant: answers by itself and routes to specialists when needed */}
                <Box
                  onClick={() => {
                    setGptDialogOpen(false);
                    if (!onAuto) gptSelector.onSelect(autoGpt.id);
                  }}
                  sx={rowSx(onAuto)}
                >
                  {renderGptAvatar(autoGpt, 28, 16)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={onAuto ? 700 : 500} noWrap>
                      {autoGpt.name || 'General'}
                    </Typography>
                    {autoGpt.is_default && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Auto — routes your request to the right specialist
                      </Typography>
                    )}
                  </Box>
                  {onAuto && <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
                </Box>
                {specialists.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600, fontSize: 10 }}
                  >
                    Specialists — pick one if you know who you need
                  </Typography>
                )}
                {specialists.map((gpt) => {
                  const active = gpt.id === gptSelector.selectedId;
                  return (
                    <Box
                      key={gpt.id}
                      onClick={() => {
                        setGptDialogOpen(false);
                        if (!active) gptSelector.onSelect(gpt.id);
                      }}
                      sx={rowSx(active)}
                    >
                      {renderGptAvatar(gpt, 28, 16)}
                      <Typography variant="body2" fontWeight={active ? 700 : 400} noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {gpt.name || 'Untitled'}
                      </Typography>
                      {active && <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
                    </Box>
                  );
                })}
                {autoGpt.is_default && (
                  <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1.25, display: 'block' }}>
                    Not sure? Stay on {autoGpt.name || 'Auto'}.
                  </Typography>
                )}
              </Dialog>
              );
            })()}
            {onWelcomeReopen && (
              <Tooltip title="Need help? Show the guide" arrow>
                <Button
                  size="small"
                  onClick={onWelcomeReopen}
                  startIcon={<HelpOutlineIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    ml: 'auto',
                    borderRadius: 999,
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.25,
                    minWidth: 0,
                    fontSize: 13,
                    color: 'text.secondary',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Guide
                </Button>
              </Tooltip>
            )}
          </Box>
        );
      })()}

      {/* File attachments preview - hidden in readonly mode */}
      {!isReadonly && selectedFiles.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {selectedFiles.map((file, index) => (
            <Box key={index} sx={{ position: 'relative', display: 'inline-block' }}>
              {file.type.startsWith('image/') ? (
                <Box
                  component="img"
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" fontWeight={500}>
                    {file.name.split('.').pop().toUpperCase()}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  fontSize: '0.65rem',
                  px: 0.5,
                  py: 0.25,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {file.name}
              </Box>
              <IconButton
                size="small"
                onClick={() => removeFile(index)}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  width: 20,
                  height: 20,
                  bgcolor: 'error.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'error.dark' },
                }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Input area */}
      {isIncognito && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            color: '#7c3aed',
            fontSize: 12,
            fontWeight: 600,
            userSelect: 'none',
            pb: 0.25,
          }}
        >
          <VisibilityOffIcon sx={{ fontSize: 14 }} />
          Incognito — not saved to history, not saved to memory
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          borderRadius: 6,
          border: 1,
          borderColor: isReadonly ? 'divider' : 'divider',
          bgcolor: isReadonly ? 'action.disabledBackground' : 'background.paper',
          transition: 'border-color 0.2s',
          minHeight: 48,
          py: 0.5,
          '&:focus-within': {
            borderColor: isReadonly ? 'divider' : 'primary.main',
          },
          // Incognito: purple-bordered, purple-tinted composer
          ...(isIncognito && !isReadonly ? {
            borderColor: 'rgba(124, 58, 237, 0.55)',
            bgcolor: 'rgba(124, 58, 237, 0.07)',
            '&:focus-within': {
              borderColor: '#7c3aed',
              boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.15)',
            },
          } : {}),
        }}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          multiple
        />
        {!isReadonly && (
          (() => {
            const canUpload = !embeddedConfig?.hideFileUpload;
            const hasLocal = !!localFiles?.enabled;
            const hasO365 = !!office365?.enabled;
            const hasGmail = !!gmail?.enabled;
            if (!canUpload && !hasLocal && !hasO365 && !hasGmail) return null;
            return (
              <>
                <Tooltip title="Add content">
                  <IconButton
                    onClick={(e) => setPlusMenuAnchor(e.currentTarget)}
                    sx={{
                      ml: 1,
                      // Theme color when at least one source is connected (folder / Office 365 / Gmail)
                      color: (
                        (localFiles?.enabled && localFiles.folderName) ||
                        (office365?.enabled && office365.connected) ||
                        (gmail?.enabled && gmail.connected)
                      ) ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={plusMenuAnchor}
                  open={Boolean(plusMenuAnchor)}
                  onClose={() => setPlusMenuAnchor(null)}
                  // Mobile: bottom sheet pinned to (0, viewport bottom) — truly
                  // full width, no anchor margins. Desktop: anchored to the +.
                  anchorReference={isMobile ? 'anchorPosition' : 'anchorEl'}
                  anchorPosition={isMobile ? { top: window.innerHeight, left: 0 } : undefined}
                  marginThreshold={0}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  PaperProps={{
                    sx: {
                      margin: 0, // kill MUI Menu's default paper margin (left gap on mobile)
                      width: { xs: '100vw', sm: 'auto' },
                      minWidth: { xs: 0, sm: 300 },
                      maxWidth: '100vw',
                      borderRadius: { xs: '14px 14px 0 0', sm: 1 },
                      '& .MuiMenuItem-root': {
                        px: { xs: 2, sm: 2 },
                      },
                    },
                  }}
                >
                  {canUpload && (
                    <MenuItem onClick={() => { setPlusMenuAnchor(null); handleFileClick(); }}>
                      <ListItemIcon><AttachFileIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>Add photos & files</ListItemText>
                    </MenuItem>
                  )}
                  {hasLocal && (() => {
                    const connected = !!localFiles.folderName;
                    return (
                      <MenuItem
                        onClick={() => {
                          setPlusMenuAnchor(null);
                          if (!localFiles.busy) localFiles.onConnect();
                        }}
                      >
                        <ListItemIcon>
                          <FolderIcon
                            fontSize="small"
                            sx={{ color: connected ? (localFiles.needsPermission ? 'warning.main' : 'primary.main') : 'inherit' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={connected ? localFiles.folderName : 'Connect local folder'}
                          secondary={
                            connected
                              ? (localFiles.needsPermission
                                  ? 'Local folder — reconnection needed'
                                  : `Local folder${localFiles.trusted ? ' · trusted (writes auto-approved)' : ''}`)
                              : 'Chrome / Edge'
                          }
                        />
                        {connected && (
                          <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                            <Tooltip title={localFiles.trusted ? 'Trusted: writes auto-approved — click to require approval' : 'Trust this folder (auto-approve writes)'}>
                              <IconButton
                                size="small"
                                sx={{ p: 0.5, color: localFiles.trusted ? 'success.main' : 'text.disabled' }}
                                onClick={() => { if (!localFiles.busy) localFiles.onToggleTrust(); }}
                              >
                                <ShieldIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Disconnect folder">
                              <IconButton
                                size="small"
                                sx={{ p: 0.5, color: 'text.disabled' }}
                                onClick={() => { if (!localFiles.busy) localFiles.onDisconnect(); }}
                              >
                                <CloseIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </MenuItem>
                    );
                  })()}
                  {hasO365 && (
                    <MenuItem onClick={() => { setPlusMenuAnchor(null); setAcctOpen('o365'); }}>
                      <ListItemIcon>
                        <CloudIcon fontSize="small" sx={{ color: office365.connected ? 'primary.main' : 'inherit' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Office 365"
                        secondary={office365.connected
                          ? `${office365.accounts?.length || 1} account(s) connected · tap to manage`
                          : 'Outlook · OneDrive · Calendar'}
                      />
                    </MenuItem>
                  )}
                  {hasGmail && (
                    <MenuItem onClick={() => { setPlusMenuAnchor(null); setAcctOpen('gmail'); }}>
                      <ListItemIcon>
                        <MailIcon fontSize="small" sx={{ color: gmail.connected ? 'primary.main' : 'inherit' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Gmail"
                        secondary={gmail.connected
                          ? `${gmail.accounts?.length || 1} account(s) connected · tap to manage`
                          : 'Search, read, drafts'}
                      />
                    </MenuItem>
                  )}
                </Menu>
                {/* Accounts manager dialog (from the + menu) */}
                <Dialog
                  open={Boolean(acctOpen)}
                  onClose={() => setAcctOpen(null)}
                  maxWidth="xs"
                  fullWidth
                  PaperProps={{ sx: { borderRadius: 2 } }}
                >
                  {(acctOpen === 'o365' || acctOpen === 'gmail') && (() => {
                    const prov = acctOpen === 'o365' ? office365 : gmail;
                    const accounts = prov.accounts?.length
                      ? prov.accounts
                      : (prov.connected ? [{ account_name: prov.accountName, account_email: null }] : []);
                    const icon = acctOpen === 'o365'
                      ? <CloudIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      : <MailIcon fontSize="small" sx={{ color: 'primary.main' }} />;
                    const signIn = acctOpen === 'o365' ? 'Sign in with Microsoft' : 'Sign in with Google';
                    return (
                      <>
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                          {icon}
                          {acctOpen === 'o365' ? 'Office 365' : 'Gmail'} — accounts
                        </DialogTitle>
                        <DialogContent dividers sx={{ px: 0 }}>
                          {accounts.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 1.5 }}>
                              No accounts connected yet.
                            </Typography>
                          )}
                          {accounts.map((a, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.25,
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              {icon}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                  {a.account_name || a.account_email || 'Account'}
                                </Typography>
                                {a.account_email && (
                                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                                    {a.account_email}
                                  </Typography>
                                )}
                              </Box>
                              <Tooltip title="Disconnect">
                                <span>
                                  <IconButton
                                    size="small"
                                    sx={{ color: 'text.disabled' }}
                                    disabled={prov.busy}
                                    onClick={() => prov.onDisconnect(a.account_email)}
                                  >
                                    <LinkOffIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          ))}
                          <Box sx={{ p: 2 }}>
                            <Button
                              fullWidth
                              variant="outlined"
                              startIcon={<AddIcon />}
                              disabled={prov.busy}
                              onClick={() => { setAcctOpen(null); prov.onConnect(); }}
                            >
                              Add account
                            </Button>
                            <Typography variant="caption" color="text.secondary" align="center" component="div" sx={{ mt: 1 }}>
                              {signIn}
                            </Typography>
                          </Box>
                        </DialogContent>
                      </>
                    );
                  })()}
                </Dialog>
              </>
            );
          })()
        )}
        <Box
          ref={inputRef}
          sx={{
            flex: 1,
            minWidth: 0, // let the input shrink on mobile instead of wrapping the row
            py: 1,
            px: (!isReadonly && !embeddedConfig?.hideFileUpload) ? 1 : 2,
          }}
        >
          <ComposerPrimitive.Input
            id="messageInput"
            placeholder={isReadonly ? "Read-only mode" : isIncognito ? "Message — incognito chat…" : "Type your message..."}
            style={{
              display: 'block',
              width: '100%',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              overflow: 'auto',
              margin: 0,
              fontSize: '1rem',
              lineHeight: 1.5,
              fontFamily: 'inherit',
              color: isReadonly ? 'inherit' : 'inherit',
              cursor: isReadonly ? 'not-allowed' : 'text',
            }}
            rows={1}
            maxRows={composerEmpty ? 1 : 5}
            autoFocus={!isReadonly}
            onPaste={handlePaste}
            disabled={isReadonly}
          />
        </Box>
        {!isReadonly && (
          <>
            {/* Speaker toggle — hidden on mobile (frees input space; re-enable from desktop) */}
            {embeddedConfig?.hasVoice && (
              <Tooltip title={(voice ? voice.enabled : embeddedConfig.voiceEnabled) ? 'Disable voice output' : 'Enable voice output'}>
                <IconButton
                  onClick={() => voice ? voice.toggle() : embeddedConfig.onToggleVoice?.()}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    color: (voice ? voice.enabled : embeddedConfig.voiceEnabled) ? 'primary.main' : 'text.disabled',
                    transition: 'color 0.2s',
                  }}
                >
                  {(voice ? voice.enabled : embeddedConfig.voiceEnabled) ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
                      <line x1="23" y1="9" x2="17" y2="15" />
                      <line x1="17" y1="9" x2="23" y2="15" />
                    </svg>
                  )}
                </IconButton>
              </Tooltip>
            )}
            {/* Mic button — always-on conversation mode (local VAD gate). Tap to start, tap to stop. Desktop: hover for languages; Mobile: long-press for languages. */}
            {usingSttPlayer && (
            <Box
              ref={micBtnRef}
              sx={{ position: 'relative', display: 'inline-flex' }}
              onMouseEnter={() => { if (!isMobile && embeddedConfig?.hasMicrophone) setShowLangMenu(true); }}
              onMouseLeave={() => { if (!isMobile) setShowLangMenu(false); }}
              onTouchStart={(e) => {
                if (!isMobile || !embeddedConfig?.hasMicrophone) return;
                if (micLongPressTimer.current) clearTimeout(micLongPressTimer.current);
                micLongPressSuppress.current = false;
                micLongPressTimer.current = setTimeout(() => {
                  micLongPressSuppress.current = true;
                  setShowLangMenu(true);
                }, 500);
              }}
              onTouchEnd={() => {
                if (micLongPressTimer.current) {
                  clearTimeout(micLongPressTimer.current);
                  micLongPressTimer.current = null;
                }
              }}
              onTouchMove={() => {
                if (micLongPressTimer.current) {
                  clearTimeout(micLongPressTimer.current);
                  micLongPressTimer.current = null;
                }
              }}
            >
              <Tooltip title={voice ? (voice.conversationMode ? 'Always-on mic is on (click to turn off)' : 'Turn on always-on mic (hands-free)') : (browserListening ? 'Listening... (click to stop)' : (sttLang ? `Voice input (${sttLang.toUpperCase()})` : 'Voice input (auto)'))}>
                <span>
                  <IconButton
                    onClick={(e) => {
                      if (micLongPressSuppress.current) {
                        micLongPressSuppress.current = false;
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      if (voice) {
                        if (voice.conversationMode) {
                          voice.setConversationMode(false);
                        } else {
                          voice.setLang(sttLang);
                          voice.setConversationMode(true).then((ok) => { if (!ok) handleMicClick(); });
                        }
                      } else {
                        handleMicClick();
                      }
                    }}
                    sx={{
                      color: voice ? (voice.conversationMode ? 'success.main' : 'text.secondary') : (browserListening ? 'error.main' : 'text.secondary'),
                      animation: (voice?.conversationMode || browserListening) ? 'pulse 1.5s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.45 },
                      },
                      // Compact on mobile (frees space for the input)
                      p: { xs: 0.75, sm: 1 },
                      '& svg': { fontSize: { xs: 20, sm: 24 } },
                    }}
                  >
                    <MicIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {showLangMenu && (
                <Box sx={{
                  position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                  mb: 0.5, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 3,
                  border: '1px solid', borderColor: 'divider',
                  display: 'flex', flexWrap: 'wrap', gap: 0.25, p: 0.5, maxWidth: 200, zIndex: 100,
                }}>
                  {[
                    { code: '', label: 'Auto' },
                    { code: 'en', label: 'EN' },
                    { code: 'it', label: 'IT' },
                    { code: 'fr', label: 'FR' },
                    { code: 'de', label: 'DE' },
                    { code: 'es', label: 'ES' },
                    { code: 'pt', label: 'PT' },
                    { code: 'ja', label: 'JA' },
                    { code: 'zh', label: 'ZH' },
                    { code: 'ko', label: 'KO' },
                    { code: 'ar', label: 'AR' },
                    { code: 'ru', label: 'RU' },
                    { code: 'nl', label: 'NL' },
                    { code: 'hi', label: 'HI' },
                  ].map(l => (
                    <Box
                      key={l.code}
                      onClick={(e) => { e.stopPropagation(); setSttLang(l.code); setShowLangMenu(false); if (voice) { voice.setLang(l.code); if (!voice.conversationMode) voice.setConversationMode(true); } }}
                      sx={{
                        px: 0.75, py: 0.25, borderRadius: 0.5, cursor: 'pointer',
                        fontSize: '11px', fontWeight: sttLang === l.code ? 700 : 400,
                        bgcolor: sttLang === l.code ? 'primary.main' : 'transparent',
                        color: sttLang === l.code ? 'primary.contrastText' : 'text.secondary',
                        '&:hover': { bgcolor: sttLang === l.code ? 'primary.dark' : 'action.hover' },
                      }}
                    >
                      {l.label}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            )}
            {/* Show Stop button when thread is running OR typewriter is animating */}
            <ThreadPrimitive.If running>
              <ComposerPrimitive.Cancel asChild>
                <IconButton
                  id="stopButton"
                  onClick={handleStopAndFocus}
                  sx={{
                    mr: 1,
                    bgcolor: 'grey.500',
                    color: 'white',
                    '&:hover': { bgcolor: 'grey.600' },
                  }}
                >
                  <StopIcon />
                </IconButton>
              </ComposerPrimitive.Cancel>
            </ThreadPrimitive.If>
            <ThreadPrimitive.If running={false}>
              {isTypewriterAnimating ? (
                <IconButton
                  id="stopButton"
                  onClick={handleStopTypewriter}
                  sx={{
                    mr: 1,
                    bgcolor: 'grey.500',
                    color: 'white',
                    '&:hover': { bgcolor: 'grey.600' },
                  }}
                >
                  <StopIcon />
                </IconButton>
              ) : (
                <Box
                  onClick={focusInput}
                  sx={{ display: 'inline-flex' }}
                >
                  {selectedFiles.length > 0 ? (
                    // When files are attached, ComposerPrimitive.Send auto-disables on
                    // empty text and ignores attachments. Send directly via the runtime
                    // so the user can submit with only a file (no typed message).
                    <IconButton
                      id="sendButton"
                      onClick={() => {
                        composerRuntime.send();
                        blurOnTouch();
                      }}
                      sx={{
                        mr: 1,
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  ) : (
                    <ComposerPrimitive.Send asChild>
                      <IconButton
                        id="sendButton"
                        onClick={blurOnTouch}
                        sx={{
                          mr: 1,
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&:disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                        }}
                      >
                        <SendIcon />
                      </IconButton>
                    </ComposerPrimitive.Send>
                  )}
                </Box>
              )}
            </ThreadPrimitive.If>
          </>
        )}
      </Box>
    </ComposerPrimitive.Root>
  );
};

// Track initialized charts globally to prevent duplicate creation across re-renders
const initializedCharts = new Map(); // chartId -> { chart: Chart instance, html: string }

// Component to render chart HTML and execute its script
const ChartRendererImpl = ({ chartHtml, chartId }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !chartHtml || !chartId) {
      return;
    }

    // Check if this exact chart is already initialized
    const existing = initializedCharts.get(chartId);
    if (existing && existing.html === chartHtml) {
      // Chart already exists, just make sure the HTML is there
      const htmlOnly = chartHtml.replace(/<script>[\s\S]*?<\/script>/, '');
      containerRef.current.innerHTML = htmlOnly;
      return;
    }

    // Destroy existing chart if it exists
    if (existing && existing.chart && typeof existing.chart.destroy === 'function') {
      existing.chart.destroy();
      initializedCharts.delete(chartId);
    }

    // Extract script and HTML separately
    const scriptMatch = chartHtml.match(/<script>([\s\S]*?)<\/script>/);
    const htmlOnly = chartHtml.replace(/<script>[\s\S]*?<\/script>/, '');

    // Set the HTML content
    containerRef.current.innerHTML = htmlOnly;

    // Execute the script after a delay to let DOM update
    if (scriptMatch && scriptMatch[1]) {
      setTimeout(() => {
        // Double-check we haven't already initialized
        if (initializedCharts.has(chartId)) {
          return;
        }

        try {
          const canvas = document.getElementById(chartId);
          if (canvas && typeof Chart !== 'undefined') {
            // Store a placeholder immediately to prevent race conditions
            initializedCharts.set(chartId, { chart: null, html: chartHtml });

            // Modify the script to capture the chart instance
            const modifiedScript = scriptMatch[1].replace(
              /new Chart\(canvas,/,
              `window.__lastChart = new Chart(canvas,`
            );
            eval(modifiedScript);

            if (window.__lastChart) {
              initializedCharts.set(chartId, { chart: window.__lastChart, html: chartHtml });
              delete window.__lastChart;
            }
          }
        } catch (e) {
          console.error('Chart script error:', e);
          initializedCharts.delete(chartId);
        }
      }, 150);
    }

    // Cleanup on unmount
    return () => {
      // Don't destroy on unmount - the chart may be re-mounted
      // Only destroy when a new chart with different HTML is created
    };
  }, [chartId, chartHtml]);

  return <div ref={containerRef} />;
};

// Memoize to prevent unnecessary re-renders
const ChartRenderer = memo(ChartRendererImpl, (prevProps, nextProps) => {
  // Only re-render if chartId changes
  return prevProps.chartId === nextProps.chartId;
});

// TypewriterText - applies typewriter effect to streaming text only
const TypewriterText = ({ text, pulsing = true }) => {
  const { displayedText, isAnimating, skipToEnd } = useGlideText(text || '', 5);
  // The run status is authoritative: when the message is no longer streaming
  // the animation is over, whatever the typewriter flag says (a stuck flag
  // would otherwise hide table export buttons / keep the fade mask forever).
  const animating = isAnimating && pulsing;
  const tableExportEnabled = useContext(TableExportEnabledContext) && !animating;

  // Update global typewriter state so Composer can access it
  useEffect(() => {
    typewriterState.isAnimating = animating;
    typewriterState.skipToEnd = skipToEnd;
  }, [animating, skipToEnd]);

  if (!displayedText) return null;
  return (
    <TableExportEnabledContext.Provider value={tableExportEnabled}>
      <IncrementalMarkdown text={displayedText} fade={animating} />
    </TableExportEnabledContext.Provider>
  );
};

// ActivityBlock - what the turn did, as ONE timeline: the real actions
// (tool runs, delegations, searches) and, for GPTs with "Show reasoning", the
// reasoning. Live: "Working… Xs" with the current action and the latest
// thought (labelled with who is thinking). Done: one line, "Worked for Xs ·
// N steps" (or "Thought for Xs"), expandable. A header marks each change of
// who is thinking (e.g. the concierge, then a delegated specialist).
const lastSentence = (text) => {
  const clean = (text || '').replace(/[*#_`>]/g, '').trim();
  const parts = clean.split(/(?<=[.!?])\s+|\n+/).map(p => p.trim()).filter(Boolean);
  return parts.pop() || '';
};

const ActivityBlock = ({ timeline = [], current, startedAt, endedAt, durationMs }) => {
  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);
  const done = durationMs != null || !!endedAt;
  const listRef = useRef(null);
  const followRef = useRef(true);

  // Opening the timeline shows the latest step; while the turn runs it keeps
  // following new steps unless the user scrolled up to read older ones
  useEffect(() => {
    if (open) followRef.current = true;
  }, [open]);
  useEffect(() => {
    const el = listRef.current;
    if (open && el && followRef.current) el.scrollTop = el.scrollHeight;
  }, [open, timeline.length, timeline[timeline.length - 1]?.text?.length]);

  // Live elapsed time while working
  useEffect(() => {
    if (done) return undefined;
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [done]);

  // A finished turn that did nothing worth showing leaves no trace
  if (done && timeline.length === 0) return null;

  const steps = timeline.filter(item => item.kind === 'action').length;
  const lastThought = [...timeline].reverse().find(item => item.kind === 'thought');
  // Who worked: a header marks each change when more than one assistant did
  const workers = new Set(timeline.map(item => item.source).filter(Boolean));
  const ms = durationMs != null ? durationMs : ((endedAt || Date.now()) - (startedAt || Date.now()));
  const secs = Math.max(1, Math.round(ms / 1000));
  const label = !done ? `Working… ${secs}s`
    : steps ? `Worked for ${secs}s · ${steps} step${steps === 1 ? '' : 's'}`
    : `Thought for ${secs}s`;

  let shownWho = null;
  return (
    <Box className="activity-block" sx={{ mb: 1.5 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', userSelect: 'none',
          color: 'text.secondary', fontSize: '0.875rem', borderRadius: 1, px: 0.5, ml: -0.5,
          '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
        }}
      >
        <Box
          component="span"
          sx={done ? {} : {
            animation: 'pulse 1.5s infinite',
            '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
          }}
        >
          {label}
        </Box>
        <Box component="span" sx={{ display: 'inline-block', transition: 'transform 0.18s', transform: open ? 'rotate(90deg)' : 'none' }}>
          ›
        </Box>
      </Box>

      {!open && !done && (
        <>
          {current && (
            <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {current}
            </Typography>
          )}
          {lastThought && lastSentence(lastThought.text) && (
            <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {lastThought.source && (
                <Box component="span" sx={{ fontStyle: 'normal', fontWeight: 600, mr: 0.75 }}>{lastThought.source} ·</Box>
              )}
              {lastSentence(lastThought.text)}
            </Typography>
          )}
        </>
      )}

      {open && (
        <Box
          ref={listRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            followRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
          }}
          sx={{ mt: 1, pl: 1.75, maxHeight: 420, overflowY: 'auto', fontSize: '0.85rem' }}
        >
          {timeline.map((item, i) => {
            const header = workers.size > 1 && !!item.source && item.source !== shownWho;
            if (item.source) shownWho = item.source;
            return (
              <Box key={i}>
                {header && (
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', mt: i ? 1.25 : 0, mb: 0.25 }}>
                    {item.source}
                  </Typography>
                )}
                {item.kind === 'action' ? (
                  <Box sx={{ my: 0.5, color: 'text.primary', display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Box component="span" sx={{ color: 'primary.main', fontSize: '0.55rem', position: 'relative', top: -2 }}>●</Box>
                    <span>{item.text}</span>
                  </Box>
                ) : (
                  <Box sx={{ color: 'text.secondary', '& p': { my: 0.5 } }}>
                    <Markdown remarkPlugins={[remarkGfm]}>{(item.text || '').trim()}</Markdown>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// Saved activity of a history message: metadata.activity, or the reasoning-only
// shape some messages were saved with before actions joined the timeline.
const savedActivity = (metadata) => {
  if (metadata?.activity?.timeline?.length) return metadata.activity;
  const sections = metadata?.reasoning?.sections;
  if (sections?.length) {
    return {
      timeline: sections.map(sec => ({ kind: 'thought', source: sec.source, text: sec.text })),
      duration_ms: metadata.reasoning.duration_ms || 0,
    };
  }
  return null;
};

// StatusDisplay - shows status messages immediately (no typewriter).
// Pulsing only while the message is streaming; a completed message renders
// mid-answer statuses as static history (trailing ones are filtered out).
const StatusDisplay = ({ statusType, text, pulsing = true }) => {
  const statusColor = statusType === 'error' ? 'error.main' :
                     statusType === 'warning' ? 'warning.main' :
                     'text.secondary';

  return (
    <Typography
      className="status-display"
      sx={{
        color: statusColor,
        fontStyle: 'italic',
        ...(pulsing ? {
          animation: 'pulse 1.5s infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.5 },
          },
        } : {}),
        pb: 1.5,
      }}
    >
      {text}
    </Typography>
  );
};

// ChartDisplay - renders charts immediately (no typewriter)
const ChartDisplay = ({ chartHtml, chartId }) => {
  return <ChartRenderer chartHtml={chartHtml} chartId={chartId} />;
};

// FileDownloadDisplay - renders file download button immediately
const FileDownloadDisplay = ({ filename, fileData, format, sizeBytes }) => {
  const [downloadState, setDownloadState] = useState('ready'); // 'ready', 'downloaded', 'failed'

  const fileIcon = format === 'excel' ? '📊' :
                   format === 'word' ? '📝' :
                   format === 'pdf' ? '📄' : '📋';

  const fileSizeKB = Math.round(sizeBytes / 1024);

  const handleDownload = () => {
    try {
      // Decode base64 and create blob
      const binaryString = atob(fileData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Determine MIME type
      const mimeTypes = {
        'excel': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'word': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'pdf': 'application/pdf',
        'csv': 'text/csv'
      };
      const mimeType = mimeTypes[format] || 'application/octet-stream';

      const blob = new Blob([bytes], { type: mimeType });
      const url = URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoking right after click() can cancel the download before the browser
      // reads the blob: it then lands under a random id with no extension.
      setTimeout(() => URL.revokeObjectURL(url), 60000);

      console.log(`✅ [FILE] Download triggered successfully`);
      setDownloadState('downloaded');
    } catch (error) {
      console.error(`❌ [FILE] Download failed:`, error);
      setDownloadState('failed');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        my: 1.5,
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        maxWidth: 360,
        boxShadow: 1,
      }}
    >
      <Typography sx={{ fontSize: '1.5rem' }}>{fileIcon}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontWeight: 500,
          fontSize: '0.875rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {filename}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {fileSizeKB} KB
        </Typography>
      </Box>
      <Box
        component="button"
        onClick={handleDownload}
        sx={{
          px: 2,
          py: 0.75,
          borderRadius: 1.5,
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.8rem',
          fontWeight: 600,
          bgcolor: downloadState === 'downloaded' ? 'success.main' :
                   downloadState === 'failed' ? 'error.main' : 'primary.main',
          color: 'white',
          '&:hover': {
            bgcolor: downloadState === 'downloaded' ? 'success.dark' :
                     downloadState === 'failed' ? 'error.dark' : 'primary.dark',
          },
        }}
      >
        {downloadState === 'downloaded' ? '✓ Done' :
         downloadState === 'failed' ? '✗ Error' : 'Download'}
      </Box>
    </Box>
  );
};

// HistoryFilePlaceholderCard - rendered when loading conversation history.
// The actual file_data is ephemeral and not stored in DB, so we show a
// disabled card to make clear the file existed but is no longer downloadable.
const HistoryFilePlaceholderCard = ({ filename, format }) => {
  const fileIcon = format === 'excel' ? '📊' :
                   format === 'word' ? '📝' :
                   format === 'pdf' ? '📄' : '📋';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        my: 1.5,
        p: 1.5,
        bgcolor: 'action.hover',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        borderStyle: 'dashed',
        maxWidth: 360,
        opacity: 0.7,
      }}
    >
      <Typography sx={{ fontSize: '1.5rem', filter: 'grayscale(0.5)' }}>{fileIcon}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontWeight: 500,
          fontSize: '0.875rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'text.secondary',
        }}>
          {filename}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
          File generato in precedenza — non più disponibile
        </Typography>
      </Box>
    </Box>
  );
};

// ToolResultText - shows tool results immediately (no typewriter)
const ToolResultText = ({ text }) => {
  if (!text) return null;
  return <StandaloneMarkdown text={text} />;
};

// Legacy fallback: handles old-style embedded content (status markers, charts in text)
const LegacyTextHandler = ({ text }) => {
  if (!text) return null;

  // Check for embedded status marker (legacy format)
  const statusMatch = text.match(/__STATUS_(LOG|WARNING|ERROR)__:([^\n]+)/);
  if (statusMatch) {
    const statusType = statusMatch[1].toLowerCase();
    const statusText = statusMatch[2];
    const textWithoutStatus = text.replace(/__STATUS_(LOG|WARNING|ERROR)__:[^\n]+\n?/, '').trim();

    return (
      <>
        {textWithoutStatus && <TypewriterText text={textWithoutStatus} />}
        <StatusDisplay statusType={statusType} text={statusText} />
      </>
    );
  }

  // Check for embedded chart HTML (legacy format)
  const chartMatch = text.match(/<div class="chart-container"[\s\S]*?<\/script>\s*\n?/);
  if (chartMatch) {
    const chartHtml = chartMatch[0];
    const beforeChart = text.substring(0, chartMatch.index).trim();
    const afterChart = text.substring(chartMatch.index + chartHtml.length).trim();

    const idMatch = chartHtml.match(/id="(chart_[^"]+)"/);
    const chartId = idMatch ? idMatch[1] : `chart_${Date.now()}`;

    return (
      <>
        {beforeChart && <TypewriterText text={beforeChart} />}
        <ChartRenderer chartHtml={chartHtml} chartId={chartId} />
        {afterChart && <LegacyTextHandler text={afterChart} />}
      </>
    );
  }

  // Pure text - apply typewriter
  return <TypewriterText text={text} />;
};

// GeneratedImageDisplay - renders AI-generated images
const GeneratedImageDisplay = ({ data, mimeType }) => {
  const dataUrl = `data:${mimeType};base64,${data}`;

  return (
    <Box
      sx={{
        my: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Box
        component="img"
        src={dataUrl}
        alt="Generated image"
        sx={{
          maxWidth: '100%',
          maxHeight: 512,
          borderRadius: 2,
          boxShadow: 2,
        }}
      />
    </Box>
  );
};

// Component to render a single part based on its type
const PartRenderer = ({ part, pulsing = true }) => {
  if (!part) return null;

  switch (part.type) {
    case 'text':
      // Text content gets typewriter effect
      return <TypewriterText text={part.text} pulsing={pulsing} />;

    case 'activity':
      return <ActivityBlock timeline={part.timeline} current={part.current} startedAt={part.startedAt} endedAt={part.endedAt} />;

    case 'status':
      // Status messages appear immediately (no typewriter, no markdown)
      return <StatusDisplay statusType={part.statusType} text={part.text} pulsing={pulsing} />;

    case 'chart':
      // Charts render immediately (no typewriter, no markdown)
      return <ChartDisplay chartHtml={part.chartHtml} chartId={part.chartId} />;

    case 'file_download':
      // File download - show download button immediately
      return <FileDownloadDisplay
        filename={part.filename}
        fileData={part.fileData}
        format={part.format}
        sizeBytes={part.sizeBytes}
      />;

    case 'question_card':
      // Clickable multiple-choice question (ask_user tool)
      return <QuestionCard
        question={part.question}
        description={part.description}
        options={part.options}
        multiple={part.multiple}
        cardId={part.cardId}
      />;

    case 'form_card':
      // Single-screen configuration form (present_form tool)
      return <ConfiguratorForm
        title={part.title}
        description={part.description}
        fields={part.fields}
        submitLabel={part.submitLabel}
        formId={part.formId}
      />;

    case 'write_confirm':
      // Local Files write approval (local_write_file tool) — diff card
      return <WriteConfirmCard
        callId={part.callId}
        path={part.path}
        isNew={part.isNew}
        trusted={part.trusted}
        oldIsBinary={part.oldIsBinary}
        oldSize={part.oldSize}
        newSize={part.newSize}
        diff={part.diff}
        preview={part.preview}
      />;

    case 'tool-result':
      // Tool results appear immediately (no typewriter)
      return <ToolResultText text={part.text} />;

    case 'image':
      // AI-generated images - render immediately
      return <GeneratedImageDisplay data={part.data} mimeType={part.mime_type || 'image/png'} />;

    default:
      // Fallback for unknown types - use legacy handler
      if (part.text) {
        return <LegacyTextHandler text={part.text} />;
      }
      return null;
  }
};

// AssistantMessageContent - renders content with a pulsing-dot fallback
// while waiting for the first token. Server-side status events (tool calls,
// reasoning, web search, ...) render in their own UI channel and replace
// this fallback as they arrive.
const AssistantMessageContent = () => {
  const messageRuntime = useMessageRuntime();
  const [content, setContent] = useState(() => messageRuntime.getState()?.content || []);
  const [isStreaming, setIsStreaming] = useState(() => {
    const code = messageRuntime.getState()?.status?.code;
    return !code || code === 'running';
  });

  useEffect(() => {
    const unsubscribe = messageRuntime.subscribe(() => {
      const state = messageRuntime.getState();
      if (state?.content) {
        setContent(state.content);
      }
      const code = state?.status?.code;
      if (code) {
        setIsStreaming(code === 'running');
      }
    });
    return unsubscribe;
  }, [messageRuntime]);

  // A completed message must not keep trailing status lines pulsing forever
  // (e.g. a heartbeat emitted during the silent wrap-up after the final text).
  // Drop status parts after the last non-status part; keep earlier ones as
  // static (non-pulsing) history of what the assistant did mid-answer.
  let renderedContent = content;
  if (!isStreaming && content.length > 0) {
    let lastNonStatus = -1;
    content.forEach((part, i) => {
      if (part.type !== 'status') lastNonStatus = i;
    });
    renderedContent = content.filter((part, i) => part.type !== 'status' || i <= lastNonStatus);
  }

  const hasContent = renderedContent && renderedContent.length > 0 && renderedContent.some(part => {
    if (part.type === 'text') return part.text && part.text.trim().length > 0;
    return true;
  });

  return (
    <TableExportEnabledContext.Provider value={!isStreaming}>
      <div className="assistant-content-container" style={{ minHeight: '24px' }}>
        {!hasContent ? (
          // Pulse only while the message is actually streaming — a message
          // aborted before any content (Stop/ESC) must not keep pulsing.
          isStreaming ? (
            <span className="thinking-pulse" aria-label="Thinking" role="status">
              <span className="thinking-pulse-dot" />
            </span>
          ) : null
        ) : (
          renderedContent.map((part, index) => (
            <PartRenderer key={index} part={part} pulsing={isStreaming} />
          ))
        )}
      </div>
    </TableExportEnabledContext.Provider>
  );
};


const AssistantMessage = () => {
  const [copied, setCopied] = useState(false);
  const embeddedConfig = useContext(EmbeddedConfigContext);
  const messageRuntime = useMessageRuntime();
  const rootRef = useRef(null);
  // Copy/Delete act on the finished answer: hidden while the turn runs.
  // The local runtime reports status.type ('running' | 'complete' | 'incomplete')
  const runningNow = () => messageRuntime.getState()?.status?.type === 'running';
  const [isRunning, setIsRunning] = useState(runningNow);
  useEffect(() => messageRuntime.subscribe(() => setIsRunning(runningNow())), [messageRuntime]);

  // Answer-space spacer (mobile only). The stylesheet gives the last
  // assistant message a tall min-height (40vh desktop / 55vh mobile) so the
  // pending answer has room below the question. On small screens that fixed
  // value makes the run-start scroll-to-bottom crop short questions. While
  // this message is streaming, size the spacer (--answer-space) to the room
  // actually left below the user bubble instead: a short question stays
  // fully visible, a long one still scrolls up (no room for both). The
  // library re-scrolls on content mutations, so a late resize self-corrects.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const vp = document.getElementById('chatScrollContainer');
    if (!vp) return;
    // Mobile only — desktop keeps the fixed 40vh spacer
    const mq = window.matchMedia('(max-width: 768px)');
    if (!mq.matches) return;

    const isStreaming = () => {
      const code = messageRuntime.getState()?.status?.code;
      return !code || code === 'running';
    };

    const apply = () => {
      if (!mq.matches || !isStreaming() || !root.parentElement) {
        root.style.removeProperty('--answer-space');
        return;
      }
      // Only the last message element carries the spacer (matches the
      // CSS :last-child / :last-of-type selectors)
      const messageEls = Array.from(root.parentElement.children).filter((el) =>
        el.classList.contains('chat-message')
      );
      if (messageEls[messageEls.length - 1] !== root) {
        root.style.removeProperty('--answer-space');
        return;
      }
      const bubble = root.previousElementSibling?.classList.contains('user-message')
        ? root.previousElementSibling
        : root.parentElement.querySelector('.user-message:last-of-type');
      let bubbleHeight = 0;
      if (bubble) {
        const bs = window.getComputedStyle(bubble);
        bubbleHeight =
          bubble.offsetHeight +
          (parseFloat(bs.marginTop) || 0) +
          (parseFloat(bs.marginBottom) || 0);
      }
      const vs = window.getComputedStyle(vp);
      const padY = (parseFloat(vs.paddingTop) || 0) + (parseFloat(vs.paddingBottom) || 0);
      const room = vp.clientHeight - padY - bubbleHeight;
      const space = Math.max(96, Math.min(vp.clientHeight * 0.55, room));
      const value = `${Math.round(space)}px`;
      // Skip redundant writes: style mutations trigger the library's
      // MutationObserver and force a re-scroll
      if (root.style.getPropertyValue('--answer-space') !== value) {
        root.style.setProperty('--answer-space', value);
      }
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(vp);
    const unsubscribe = messageRuntime.subscribe(apply);
    return () => {
      ro.disconnect();
      unsubscribe();
      root.style.removeProperty('--answer-space');
    };
  }, [messageRuntime]);

  const handleCopy = async (e) => {
    e.stopPropagation();
    const messageRoot = e.currentTarget.closest('[data-role="assistant"]');
    const contentElem = messageRoot.querySelector('.chat-assistant-text');
    const success = await copyMessageContent(contentElem);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm('Delete this message?')) return;

    const messageRoot = e.currentTarget.closest('[data-role="assistant"]');
    const messageId = messageRoot.getAttribute('data-message-id');
    if (!messageId) {
      alert('Cannot delete message - ID not found');
      return;
    }

    try {
      const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

      // Use different endpoint and auth for embedded vs full mode
      let response;
      if (embeddedConfig?.isEmbedded && embeddedConfig?.shareToken && embeddedConfig?.customGptId) {
        // Embedded mode: use public endpoint with share token
        response = await fetch(`${API_URL}/public/cme/${embeddedConfig.customGptId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: messageId, mode: embeddedConfig.mode || 'full' },
            token: embeddedConfig.shareToken
          })
        });
      } else {
        // Full mode: use protected endpoint with auth token
        const token = localStorage.getItem('cme_token');
        response = await fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: messageId, mode: 'full' }
          })
        });
      }

      if (response.ok) {
        messageRoot.remove();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message');
    }
  };

  return (
    <MessagePrimitive.Root
      ref={rootRef}
      style={{ display: 'flex' }}
      className="chat-message-enter chat-message assistant-message"
      data-role="assistant"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box
          className="chat-assistant-text aui-md"
          sx={{
            color: 'text.primary',
            maxWidth: 'none',
            wordBreak: 'break-word',
          }}
        >
          <AssistantMessageContent />
        </Box>
        {/* Copy and Delete buttons - always show in non-embedded mode */}
        <Box
          className="message-tools-container"
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-start',
            gap: 0.5,
            mt: 0.5,
          }}
        >
          {!isRunning && (embeddedConfig?.showCopyButton !== false) && (
            <Tooltip title={copied ? "Copied!" : "Copy message"}>
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
              >
                {copied ? <CheckIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          )}
          {!isRunning && (embeddedConfig?.showDeleteButton !== false) && (
            <Tooltip title="Delete message">
              <IconButton
                onClick={handleDelete}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </MessagePrimitive.Root>
  );
};

const UserMessage = () => {
  const messageFilesContext = useContext(MessageFilesContext);
  const { messageFiles } = messageFilesContext || { messageFiles: {} };
  const messageRuntime = useMessageRuntime();
  const embeddedConfig = useContext(EmbeddedConfigContext);
  const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

  // Files for THIS message, keyed by message id at send time. Immune to DOM
  // ordering, history messages, and deletions (the old DOM-index mapping
  // drifted and showed the previous message's picture).
  const messageId = messageRuntime.getState()?.id;
  const files = (messageFiles && messageId && messageFiles[messageId]) || [];

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm('Delete this message?')) return;

    const messageRoot = e.currentTarget.closest('[data-role="user"]');
    const messageId = messageRoot.getAttribute('data-message-id');
    if (!messageId) {
      alert('Cannot delete message - ID not found');
      return;
    }

    try {
      const API_URL = embeddedConfig?.apiUrl || window.INSIGHT_CONFIG?.API_URL || 'http://localhost:8000';

      // Use different endpoint and auth for embedded vs full mode
      let response;
      if (embeddedConfig?.isEmbedded && embeddedConfig?.shareToken && embeddedConfig?.customGptId) {
        // Embedded mode: use public endpoint with share token
        response = await fetch(`${API_URL}/public/cme/${embeddedConfig.customGptId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: messageId, mode: embeddedConfig.mode || 'full' },
            token: embeddedConfig.shareToken
          })
        });
      } else {
        // Full mode: use protected endpoint with auth token
        const token = localStorage.getItem('cme_token');
        response = await fetch(`${API_URL}/cme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'messages.delete',
            params: { message_id: messageId, mode: 'full' }
          })
        });
      }

      if (response.ok) {
        messageRoot.remove();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message');
    }
  };

  return (
    <MessagePrimitive.Root
      style={{ display: 'flex', justifyContent: 'flex-end' }}
      className="chat-message-enter chat-message user-message"
      data-role="user"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '75%' }}>
        <Paper
          elevation={0}
          sx={{
            // theme's user-bubble colors when it defines them, neutral otherwise
            bgcolor: (t) => t.palette.background.userBubble || t.palette.action.hover,
            border: (t) => (t.palette.background.userBubbleBorder
              ? `1px solid ${t.palette.background.userBubbleBorder}` : undefined),
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          {/* File attachments - render before text like nnaico */}
          {files.length > 0 && (
            <Box sx={{ p: 1.5, pb: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {files.map((att, index) => {
                const isImage = att.content_type && att.content_type.startsWith('image/');
                // Use blob URL directly if available, otherwise construct API URL
                const imageUrl = attachmentUrl(att.id);

                if (isImage) {
                  return (
                    <Box key={index} sx={{ overflow: 'hidden', borderRadius: 3 }}>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={att.filename}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          objectFit: 'contain',
                          maxWidth: 250,
                          maxHeight: 200,
                        }}
                      />
                    </Box>
                  );
                } else {
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        p: 1,
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2">{att.filename}</Typography>
                    </Box>
                  );
                }
              })}
            </Box>
          )}

          <Box
            className="chat-user-text"
            sx={{
              // a filled (solid-color) bubble brings its own ink
              color: (t) => t.palette.background.userBubbleText || t.palette.text.primary,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              p: 1.5,
            }}
          >
            <MessagePrimitive.Parts />
          </Box>
        </Paper>
        {/* Tools container - always render for consistent spacing */}
        <Box
          className="message-tools-container"
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
            gap: 0.5,
            mt: 0.5,
          }}
        >
          {(embeddedConfig?.showDeleteButton !== false) && (
            <Tooltip title="Delete message">
              <IconButton
                onClick={handleDelete}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </MessagePrimitive.Root>
  );
};

/**
 * Renders nothing. Listens for `question_answer` events (dispatched by
 * QuestionCard when the user clicks an option) and appends the selected answer
 * as a new user message via the assistant-ui thread runtime. The LLM then
 * resumes the conversation with full context.
 *
 * MUST be rendered inside <AssistantRuntimeProvider>.
 */
export function QuestionAutoSubmitListener() {
  const runtime = useThreadRuntime();
  useEffect(() => {
    const handler = (e) => {
      const text = e?.detail?.text;
      if (!text) return;
      try {
        // CreateAppendMessage accepts a plain string (normalized to a text part)
        runtime.append(text);
      } catch (err) {
        console.error('[QuestionAutoSubmitListener] append failed:', err);
      }
    };
    chatEvents.addEventListener('question_answer', handler);
    return () => chatEvents.removeEventListener('question_answer', handler);
  }, [runtime]);
  return null;
}
